import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Award, CheckCircle2, Lock } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useClubs } from "@/modules/leagues/hooks/useLeagueAdmin";
import { useBonusQuestionsConfig, useMyBonusAnswer, useSaveMyBonusAnswer } from "@/modules/leagues/hooks/useBonusQuestions";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { EmptyState } from "@/components/organisms/EmptyState";
import { TrophyIllustration } from "@/components/atoms/illustrations";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import {
  BONUS_QUESTION_CATALOG,
  BONUS_QUESTION_ORDER,
  hasAnyActiveQuestion,
  isDeadlinePassed,
  type BonusQuestionType,
} from "@/modules/leagues/types/bonusQuestions";

function formatDeadline(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * Tela do participante pra responder as Perguntas Bônus (pedido do
 * usuário): abre pelo aviso no dashboard, escolhe um clube por pergunta
 * ativa, pode alterar livremente até o prazo. Depois do prazo, tudo vira
 * somente-leitura — a Firestore Rule já bloqueia a escrita de verdade,
 * isto aqui só evita a pessoa tentar e levar um erro de permissão.
 */
export function BonusQuestionsAnswerPage() {
  const { league } = useLeagueContext();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: config, isLoading: loadingConfig } = useBonusQuestionsConfig(league.id);
  const { data: myAnswer, isLoading: loadingAnswer } = useMyBonusAnswer(league.id);
  const { data: clubs, isLoading: loadingClubs } = useClubs(league.competitionId);
  const saveAnswer = useSaveMyBonusAnswer(league.id);

  const [selections, setSelections] = useState<Partial<Record<BonusQuestionType, string>>>({});

  useEffect(() => {
    if (myAnswer) setSelections(myAnswer.answers);
  }, [myAnswer]);

  const locked = isDeadlinePassed(config);
  const activeQuestions = BONUS_QUESTION_ORDER.filter((t) => config?.questions[t]?.active);

  function handleSave() {
    saveAnswer.mutate(selections, {
      onSuccess: () => toast({ title: "Respostas salvas!" }),
      onError: (err) =>
        toast({
          title: "Não foi possível salvar",
          description: err instanceof Error ? err.message : "Tente novamente.",
          variant: "destructive",
        }),
    });
  }

  const isLoading = loadingConfig || loadingAnswer || loadingClubs;

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(ROUTES.home)}
          aria-label="Voltar"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="font-display text-base font-bold">Perguntas Bônus — {league.nome}</h1>
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {!isLoading && !hasAnyActiveQuestion(config) && (
        <EmptyState
          illustration={TrophyIllustration}
          title="Nenhuma pergunta bônus ativa"
          description="O administrador da liga ainda não ativou nenhuma pergunta sobre o desfecho do campeonato."
        />
      )}

      {!isLoading && hasAnyActiveQuestion(config) && (
        <>
          {config?.scored ? (
            <div className="flex items-center gap-3 rounded-2xl border border-success/30 bg-success/10 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
              <p className="text-sm">
                O campeonato encerrou e as Perguntas Bônus já foram pontuadas. Confira o resultado de cada pergunta abaixo.
              </p>
            </div>
          ) : locked ? (
            <div className="flex items-center gap-3 rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3">
              <Lock className="h-5 w-5 shrink-0 text-warning" />
              <p className="text-sm">
                O prazo de resposta encerrou em {config?.deadline && formatDeadline(config.deadline)}. Suas respostas ficaram travadas.
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary-50 px-4 py-3 dark:bg-primary-900/20">
              <Award className="h-5 w-5 shrink-0 text-primary" />
              <p className="text-sm">
                Responda até <strong>{config?.deadline && formatDeadline(config.deadline)}</strong>. Você pode alterar suas respostas
                quantas vezes quiser até lá.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {activeQuestions.map((type) => {
              const resultClubId = config?.finalResult?.[type];
              const myClubId = selections[type];
              const gotItRight = config?.scored && resultClubId && myClubId === resultClubId;
              return (
                <div key={type} className="rounded-2xl border border-border bg-card p-4">
                  <p className="mb-1 text-sm font-semibold">{BONUS_QUESTION_CATALOG[type].label}</p>
                  <p className="mb-3 text-[11px] text-muted-foreground">
                    {BONUS_QUESTION_CATALOG[type].helpText} · vale {config?.questions[type]?.pontos} pts
                  </p>
                  <select
                    value={myClubId ?? ""}
                    disabled={locked}
                    onChange={(e) => setSelections((prev) => ({ ...prev, [type]: e.target.value || undefined }))}
                    className="flex h-11 w-full rounded-xl border border-input bg-surface px-3 text-sm disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Escolher clube...</option>
                    {clubs?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {config?.scored && (
                    <p className={`mt-2 text-xs font-semibold ${gotItRight ? "text-success" : "text-muted-foreground"}`}>
                      {gotItRight ? `Você acertou! +${config.questions[type]?.pontos} pts` : "Você não acertou esta pergunta."}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {!locked && !config?.scored && (
            <Button className="w-full" onClick={handleSave} disabled={saveAnswer.isPending}>
              {saveAnswer.isPending ? "Salvando..." : "Salvar respostas"}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
